import React, { useState, useEffect } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import {
  FormControl,
  Select,
  MenuItem,
  Breadcrumbs,
  Typography,
} from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import Pagination from 'material-ui-flat-pagination';
import { makeStyles } from '@material-ui/core/styles';
import {
  authenticationTokenSelector,
  hsAuditServiceSelector,
} from '@selectors/global';
import {
  fetchLetterJobDetails,
  fetchLetterJobDetailsCount,
} from '../../api/internalAdministration';
import PropTypes from 'prop-types';
import { LinkCellRenderer } from '@trueprodigy/components';
import 'ag-grid-community/styles/ag-grid.css';
import { fetchMostCurrentPropertyByYear } from '../../../appraisal/actions/property';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';

const useStyles = makeStyles((theme) => ({
  input: {
    width: '54px',
    margin: '0 10px 0 0',
    '& > div > input': {
      padding: 7,
      color: '#757575',
    },
    '& > div > fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.15)',
    },
  },
  pagination: {
    paddingLeft: theme.spacing(2),
    marginRight: theme.spacing(6),
    '& > button:not(:first-child):not(:last-child)': {
      padding: theme.spacing(0.5, 2),
      borderRadius: 3,
    },
    '& > .MuiFlatPageButton-rootCurrent': {
      backgroundColor: '#142bad',
      color: '#ffffff',
    },
  },
}));

const Container = styled.div`
  padding: 20px;
  background-color: white;
`;

const FilterSection = styled.div`
  margin-bottom: 20px;
`;

const GridWrapper = styled.div`
  height: calc(100vh - 275px);
  width: 100%;
`;

const BreadcrumbsWrapper = styled.div`
  margin-bottom: 20px;
  a {
    color: #3949ab !important;
  }
`;

const JobDetails = ({ hsAuditService, authenticationToken }) => {
  const classes = useStyles();
  const { jobId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [status, setStatus] = useState(location.state?.filter || 'all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const dispatch = useDispatch();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('error');

  const columnDefs = [
    {
      headerName: 'Result ID',
      field: 'resultID',
      width: 120,
      suppressSizeToFit: true,
    },
    {
      headerName: 'Job ID',
      field: 'jobID',
      width: 120,
      suppressSizeToFit: true,
    },
    {
      headerName: 'Property ID',
      field: 'pID',
      width: 150,
      suppressSizeToFit: true,
      cellRenderer: LinkCellRenderer,
      cellRendererParams: {
        onClick: async (_, data) => {
          // Fetch most recent version and roll correction since we don't have it and need it to redirect
          const { results } = await dispatch(
            fetchMostCurrentPropertyByYear({
              pid: data.pID,
              pYear: data.pYear,
            }),
          );
          const { pid, pYear, pRollCorr, pVersion } = results[0];
          window.open(
            `/appraisal/property/${pid}/${pYear}/${pRollCorr}/${pVersion}/Summary`,
            '_blank',
          );
        },
      },
    },
    {
      headerName: 'Year',
      field: 'pYear',
      width: 100,
      suppressSizeToFit: true,
    },
    {
      headerName: 'Created Date',
      field: 'createdDt',
      width: 180,
      suppressSizeToFit: true,
      valueFormatter: (params) => {
        return params.value ? new Date(params.value).toLocaleString() : '';
      },
    },
    {
      headerName: 'Start Date',
      field: 'startDt',
      width: 180,
      suppressSizeToFit: true,
      valueFormatter: (params) => {
        return params.value ? new Date(params.value).toLocaleString() : '';
      },
    },
    {
      headerName: 'End Date',
      field: 'endDt',
      width: 180,
      suppressSizeToFit: true,
      valueFormatter: (params) => {
        return params.value ? new Date(params.value).toLocaleString() : '';
      },
    },
    {
      headerName: 'Elapsed Time (s)',
      width: 140,
      suppressSizeToFit: true,
      valueGetter: ({ data }) => {
        const startDt = new Date(data.startDt);
        const endDt = new Date(data.endDt);
        return `${(endDt - startDt) / 1000}s`;
      },
    },
    {
      headerName: 'Status',
      field: 'successful',
      width: 120,
      suppressSizeToFit: true,
      valueFormatter: (params) => {
        return params.value === 1 ? 'Success' : 'Error';
      },
      cellStyle: (params) => {
        return {
          color: params.value === 1 ? '#43a047' : '#d32f2f',
        };
      },
    },
    {
      headerName: 'Error Message',
      field: 'errorMessage',
      flex: 1,
      minWidth: 300,
    },
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      // First, get the count.
      const countResponse = await fetchLetterJobDetailsCount({
        hsAuditService,
        authenticationToken,
        jobID: jobId,
        status,
      });
      const totalRecords = countResponse.results?.[0]?.totalRecords || 0;
      setTotalCount(totalRecords);
      // Then get the actual data
      const response = await fetchLetterJobDetails({
        hsAuditService,
        authenticationToken,
        jobID: jobId,
        page,
        pageSize,
        status,
      });
      setRowData(response.results || []);
      setError(null);
    } catch (err) {
      console.error('Error loading job details:', err);
      setError('Failed to load job details');
      // Try to extract a more specific error message from the API response
      let apiErrorMsg = 'Failed to load job details';
      if (err?.response?.data?.message) {
        apiErrorMsg = err.response.data.message;
      } else if (err?.message) {
        apiErrorMsg = err.message;
      }
      // If the error is a generic fetch/network error, use the fallback message
      if (
        typeof apiErrorMsg === 'string' &&
        apiErrorMsg.trim().toLowerCase() === 'failed to fetch'
      ) {
        apiErrorMsg = 'Failed to load job details';
      }
      setSnackbarMessage(apiErrorMsg);
      setSnackbarType('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [jobId, status, page, pageSize]);

  // If filter value is passed in the URL, set the status with it
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const filter = query.get('filter');
    if (filter) setStatus(filter);
  }, [location]);

  return (
    <Container>
      <BreadcrumbsWrapper>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link to="/appraisal-process-manager/mass-letter-runs">
            Mass Letter Runs
          </Link>
          <Typography color="textPrimary">Run Details</Typography>
          <Typography color="textPrimary">{jobId}</Typography>
        </Breadcrumbs>
      </BreadcrumbsWrapper>

      <FilterSection>
        <FormControl variant="outlined" style={{ width: 200 }}>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            displayEmpty
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="error">Errors</MenuItem>
          </Select>
        </FormControl>
      </FilterSection>

      {error && (
        <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>
      )}

      <GridWrapper className="ag-theme-material">
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
          frameworkComponents={{
            linkCellRenderer: LinkCellRenderer,
          }}
          overlayLoadingTemplate={
            loading ? 'Loading job details...' : undefined
          }
          overlayNoRowsTemplate={
            !loading && !rowData.length ? 'No records found' : undefined
          }
        />
      </GridWrapper>

      <PaginationContainer>
        <Pagination
          className={classes.pagination}
          limit={pageSize}
          offset={(page - 1) * pageSize}
          total={totalCount}
          onClick={(_e, newOffset) => {
            const newPage = Math.floor(newOffset / pageSize) + 1;
            setPage(newPage);
          }}
          currentPageColor="primary"
        />
      </PaginationContainer>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        style={{ marginTop: '64px', zIndex: 9999 }}
      >
        <SnackbarContent
          style={{
            backgroundColor: snackbarType === 'error' ? '#d32f2f' : '#43a047',
            minWidth: '250px',
            display: 'flex',
            justifyContent: 'space-between',
            boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
          }}
          message={
            <span style={{ flex: 1, color: 'white' }}>{snackbarMessage}</span>
          }
        />
      </Snackbar>
    </Container>
  );
};

JobDetails.propTypes = {
  hsAuditService: PropTypes.string.isRequired,
  authenticationToken: PropTypes.string.isRequired,
};

const mapStateToProps = createStructuredSelector({
  authenticationToken: authenticationTokenSelector,
  hsAuditService: hsAuditServiceSelector,
});

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 15px;
  padding: 10px 20px;
  background: white;
  position: sticky;
  bottom: 0;
  z-index: 1;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
`;

export default connect(mapStateToProps)(JobDetails);
